import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { ALLURE_TABLE, zoneDepuisApi, type ZoneRow, type ZoneRowApi } from '../Construction/allureTable.ts';

// Couleur de chaque zone d'allure (code des parametres d'allures), du plus
// intense (rouge) au plus facile (bleu-vert). Utilisee partout ou des
// seances sont affichees : Calendrier, Construction, Modeles.
export const COULEURS_ZONE: Record<string, string> = {
  VMA: '#d32f2f',
  '3K': '#e64a19',
  '5K': '#f57c00',
  '10K': '#f9a825',
  HP: '#388e3c',
  MP: '#1976d2',
  STEADY: '#00acc1',
  EASY: '#00897b',
  RECUP: '#757575',
};

export const COULEUR_INCONNUE = '#9e9e9e';

// Ordre d'affichage de la legende.
const ORDRE_ZONES = ['VMA', '3K', '5K', '10K', 'HP', 'MP', 'STEADY', 'EASY', 'RECUP'];

// Table des allures de l'utilisateur telle que mise en cache par
// useAllureTable (sessionStorage), a defaut la table par defaut : permet de
// retrouver le code d'une zone a partir de son nom sans appel serveur.
let cacheBrut: string | null = null;
let cacheTable: ZoneRow[] = ALLURE_TABLE;

function tableCourante(): ZoneRow[] {
  try {
    const brut = sessionStorage.getItem('allureTable');
    if (brut !== cacheBrut) {
      cacheBrut = brut;
      cacheTable = brut ? (JSON.parse(brut).zones as ZoneRowApi[]).map(zoneDepuisApi) : ALLURE_TABLE;
    }
  } catch {
    cacheTable = ALLURE_TABLE;
  }
  return cacheTable;
}

export function couleurPourCode(code?: string | null): string {
  return (code && COULEURS_ZONE[code]) || COULEUR_INCONNUE;
}

/**
 * Couleur d'une zone a partir de son nom (ex : "Allure semi"). Effort
 * progressif ("Allure semi → Allure 10 km") : couleur de la zone de depart.
 */
export function couleurPourNom(nomComplet?: string | null, table?: ZoneRow[]): string {
  const nom = nomComplet?.split(' → ')[0];
  if (!nom) return COULEUR_INCONNUE;
  const zone = (table ?? tableCourante()).find((z: ZoneRow) => z.nom === nom) ?? ALLURE_TABLE.find((z: ZoneRow) => z.nom === nom);
  return couleurPourCode(zone?.code);
}

/** Nom de zone precede d'une pastille de sa couleur (cellules de tableau). */
export function ZoneChip({ nom, table }: { nom?: string | null; table?: ZoneRow[] }) {
  if (!nom) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Box
        component="span"
        sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: couleurPourNom(nom, table) }}
      />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nom}</Box>
    </Box>
  );
}

/** Legende des couleurs de zones (noms issus des parametres d'allures). */
export function LegendeZones({ table, titre = 'Zone principale :' }: { table?: ZoneRow[]; titre?: string }) {
  const zones = table ?? tableCourante();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      {titre && (
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {titre}
        </Typography>
      )}
      {ORDRE_ZONES.map((code) => {
        const nom = zones.find((z: ZoneRow) => z.code === code)?.nom ?? ALLURE_TABLE.find((z: ZoneRow) => z.code === code)?.nom ?? code;
        return (
          <Box key={code} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: COULEURS_ZONE[code] }} />
            <Typography variant="caption">{nom}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}
